import { lockScroll, unlockScroll } from '@theme/utilities';

const CARDS_PER_PAGE = 3;
const PREVIEW_SECONDS = 5;

class ProductVideoCarousel extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-video-viewport]');
    this.cards = [...this.querySelectorAll('[data-video-card]')];
    this.dialog = this.querySelector('[data-video-dialog]');
    this.player = this.querySelector('[data-video-player]');
    if (!this.viewport || !this.cards.length || !this.dialog || !this.player) return;

    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.index = 0;
    this.windowStart = 0;
    this.visible = false;
    this.cardFocused = false;
    this.dragging = false;
    this.suppressClick = false;
    this.scrolling = false;
    this.autoScrolling = false;
    this.generation = 0;
    this.failed = new Set();
    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = this.motion.matches;
    this.maxWindowStart = Math.max(0, this.cards.length - CARDS_PER_PAGE);

    this.addEventListener('click', (event) => this.handleClick(event), options);
    this.viewport.addEventListener('pointerdown', (event) => this.startDrag(event), options);
    document.addEventListener('pointermove', (event) => this.moveDrag(event), options);
    document.addEventListener('pointerup', (event) => this.endDrag(event), options);
    document.addEventListener('pointercancel', (event) => this.endDrag(event), options);
    this.viewport.addEventListener('lostpointercapture', (event) => {
      // Touch starts with implicit capture on the play button. Its transfer to
      // the viewport is part of starting a drag, not a canceled gesture.
      if (event.target === this.viewport) this.endDrag(event);
    }, options);
    this.viewport.addEventListener('dragstart', (event) => event.preventDefault(), options);
    this.viewport.addEventListener('wheel', (event) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      this.cancelScroll();
      this.stopPreview();
      this.cardFocused = false;
    }, { ...options, passive: true });
    this.addEventListener('keydown', () => { this.pointerInteraction = false; }, options);
    this.addEventListener('focusin', (event) => {
      if (!event.target.closest('[data-video-open]')) return;
      if (this.pointerInteraction) return;
      this.cardFocused = true;
      this.stopPreview();
    }, options);
    this.addEventListener('focusout', (event) => {
      if (!event.target.closest('[data-video-open]')) return;
      this.cardFocused = !this.pointerInteraction && Boolean(event.relatedTarget?.closest('[data-video-open]'));
      if (!this.cardFocused) this.startPreview();
    }, options);
    this.viewport.addEventListener('scroll', () => this.handleScroll(), { ...options, passive: true });
    this.dialog.addEventListener('close', () => this.closePlayer(), options);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stopPreview();
      else this.startPreview();
    }, options);
    this.motion.addEventListener('change', () => {
      this.paused = this.motion.matches;
      if (this.paused) this.stopPreview();
      else this.startPreview();
    }, options);
    document.addEventListener('shopify:block:select', (event) => {
      const index = this.cards.findIndex((card) => card === event.target || card.contains(event.target));
      if (index < 0) return;
      this.paused = true;
      this.select(index);
    }, options);

    // Posters are set once the carousel is near the viewport so they don't
    // compete with the product image on first load.
    this.posterObserver = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      this.posterObserver.disconnect();
      this.querySelectorAll('video[data-poster]').forEach((video) => {
        video.poster = video.dataset.poster;
        video.removeAttribute('data-poster');
      });
    }, { rootMargin: '600px 0px' });
    this.posterObserver.observe(this);

    this.intersection = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
      if (this.visible) this.startPreview();
      else this.stopPreview();
    }, { threshold: [0, 0.2] });
    this.intersection.observe(this);
    this.resize = new ResizeObserver(() => this.layout());
    this.resize.observe(this.viewport);
    this.layout();
  }

  disconnectedCallback() {
    this.stopPreview();
    this.cancelScroll();
    this.dragPointerId = undefined;
    this.abort?.abort();
    this.intersection?.disconnect();
    this.posterObserver?.disconnect();
    this.resize?.disconnect();
    this.player?.pause();
    if (this.dialog) {
      if (this.dialog.open) this.dialog.close();
      unlockScroll(this.dialog);
    }
  }

  layout() {
    const width = this.cards[0].getBoundingClientRect().width;
    const gap = parseFloat(getComputedStyle(this.viewport).columnGap) || 0;
    const stride = width + gap;
    if (!width || Math.abs(stride - (this.stride || 0)) < 0.1) return;
    this.cancelScroll();
    this.stride = stride;
    this.setScrollPosition(this.windowStart * this.stride);
    this.updateVisibleCards();
    if (this.dragPointerId !== undefined) {
      this.dragStartScrollLeft = this.viewport.scrollLeft;
      this.dragStartX = this.lastPointerX;
      this.dragDistance = 0;
    } else this.startPreview();
  }

  updateVisibleCards() {
    this.cards.forEach((card, index) => {
      const visible = index >= this.windowStart && index < this.windowStart + CARDS_PER_PAGE;
      card.inert = !visible;
      card.setAttribute('aria-hidden', String(!visible));
    });
  }

  startDrag(event) {
    if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
    if (this.dragPointerId !== undefined) return;
    this.pointerInteraction = true;
    this.cardFocused = false;
    this.suppressClick = false;
    this.cancelScroll();
    this.stopPreview();
    this.dragPointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.lastPointerX = event.clientX;
    this.dragStartY = event.clientY;
    this.dragStartScrollLeft = this.viewport.scrollLeft;
    this.dragStartWindow = this.nearestWindow();
    this.dragDistance = 0;
    this.dragging = false;
  }

  moveDrag(event) {
    if (event.pointerId !== this.dragPointerId) return;
    const distance = event.clientX - this.dragStartX;
    this.lastPointerX = event.clientX;
    this.dragDistance = distance;
    if (!this.dragging) {
      const verticalDistance = Math.abs(event.clientY - this.dragStartY);
      if (verticalDistance > Math.abs(distance) && verticalDistance >= 8) {
        this.endDrag(event, true);
        return;
      }
      if (Math.abs(distance) < 8) return;
    }
    if (!this.dragging) {
      this.dragging = true;
      this.viewport.setPointerCapture(event.pointerId);
      this.viewport.classList.add('is-dragging');
    }
    if (event.cancelable) event.preventDefault();
    this.setScrollPosition(this.dragStartScrollLeft - distance);
  }

  endDrag(event, canceled = false) {
    if (event.pointerId !== this.dragPointerId) return;
    canceled ||= event.type === 'pointercancel' || event.type === 'lostpointercapture';
    this.dragPointerId = undefined;
    if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
    const didDrag = this.dragging;
    this.viewport.classList.remove('is-dragging');
    this.dragging = false;
    this.suppressClick = didDrag;
    const direction = this.dragDistance < 0 ? 1 : -1;
    const targetStart = this.clampWindow(didDrag && !canceled && Math.abs(this.dragDistance) >= 30
      ? this.dragStartWindow + direction : this.dragStartWindow);
    const index = targetStart === this.dragStartWindow ? this.index
      : direction > 0 ? targetStart + CARDS_PER_PAGE - 1 : targetStart;
    this.moveWindow(targetStart, index);
  }

  select(index) {
    index = (index + this.cards.length) % this.cards.length;
    let start = this.windowStart;
    if (index < start) start = index;
    else if (index >= start + CARDS_PER_PAGE) start = index - CARDS_PER_PAGE + 1;
    this.moveWindow(this.clampWindow(start), index);
  }

  clampWindow(start) {
    return Math.max(0, Math.min(this.maxWindowStart, start));
  }

  nearestWindow() {
    return this.clampWindow(Math.round(this.viewport.scrollLeft / this.stride));
  }

  cancelScroll() {
    clearTimeout(this.scrollTimer);
    cancelAnimationFrame(this.scrollFrame);
    this.autoScrolling = false;
    this.scrolling = false;
  }

  setScrollPosition(left) {
    this.viewport.scrollLeft = Math.max(0, Math.min(this.maxWindowStart * this.stride, left));
    this.expectedScrollLeft = this.viewport.scrollLeft;
  }

  moveWindow(start, index) {
    this.cancelScroll();
    this.stopPreview();
    this.windowStart = this.clampWindow(start);
    this.index = Math.max(this.windowStart, Math.min(this.cards.length - 1,
      this.windowStart + CARDS_PER_PAGE - 1, index));
    const left = this.windowStart * this.stride;
    if (Math.abs(this.viewport.scrollLeft - left) <= 1 || this.motion.matches) {
      this.completeScroll(left);
      return;
    }
    this.scrolling = true;
    this.autoScrolling = true;
    const from = this.viewport.scrollLeft;
    const began = performance.now();
    const step = (now) => {
      const progress = Math.min(1, (now - began) / 280);
      this.setScrollPosition(from + (left - from) * (1 - (1 - progress) ** 3));
      if (progress < 1) this.scrollFrame = requestAnimationFrame(step);
      else this.completeScroll(left);
    };
    this.scrollFrame = requestAnimationFrame(step);
  }

  handleScroll() {
    if (this.dragPointerId !== undefined || this.autoScrolling
      || Math.abs(this.viewport.scrollLeft - this.expectedScrollLeft) < 1) return;
    if (!this.scrolling) this.stopPreview();
    this.scrolling = true;
    clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(() => this.finishScroll(), 140);
  }

  finishScroll() {
    if (!this.scrolling || this.dragPointerId !== undefined || this.autoScrolling) return;
    clearTimeout(this.scrollTimer);
    const start = this.nearestWindow();
    const index = start === this.windowStart ? this.index
      : start > this.windowStart ? start + CARDS_PER_PAGE - 1 : start;
    this.moveWindow(start, index);
  }

  completeScroll(left) {
    this.setScrollPosition(left);
    this.autoScrolling = false;
    this.scrolling = false;
    this.updateVisibleCards();
    this.startPreview();
  }

  stopPreview() {
    this.generation = (this.generation || 0) + 1;
    clearTimeout(this.previewTimer);
    clearTimeout(this.loadTimer);
    this.previewAbort?.abort();
    this.activeVideo = null;
    this.cards?.forEach((card) => {
      card.querySelector('[data-video-preview]')?.pause();
      card.classList.remove('is-active');
    });
  }

  preloadVideo(index) {
    const video = this.cards[index]?.querySelector('[data-video-preview]');
    if (!video || video.readyState > HTMLMediaElement.HAVE_NOTHING) return;
    video.preload = 'auto';
    video.load();
  }

  startPreview() {
    if (!this.isConnected || !this.visible || document.hidden || this.paused || this.dialog.open || this.scrolling || this.cardFocused
      || this.dragPointerId !== undefined
      || this.activeVideo) return;
    const playable = this.cards.findIndex((card, index) => index >= this.index
      && !this.failed.has(index) && card.querySelector('[data-video-preview]'));
    if (playable < 0) {
      const first = this.cards.findIndex((card, index) => !this.failed.has(index)
        && card.querySelector('[data-video-preview]'));
      if (first >= 0) this.select(first);
      else {
        this.paused = true;
      }
      return;
    }
    if (playable !== this.index) {
      this.select(playable);
      return;
    }

    const card = this.cards[this.index];
    const video = card.querySelector('[data-video-preview]');
    const generation = this.generation;
    this.activeVideo = video;
    this.previewAbort = new AbortController();
    const options = { signal: this.previewAbort.signal };
    const advance = () => {
      if (generation === this.generation) this.select(this.index + 1);
    };
    const fail = () => {
      if (generation !== this.generation) return;
      this.failed.add(this.index);
      advance();
    };
    const schedule = () => {
      clearTimeout(this.loadTimer);
      clearTimeout(this.previewTimer);
      this.preloadVideo((this.index + 1) % this.cards.length);
      const remaining = Math.max(0, PREVIEW_SECONDS - video.currentTime);
      this.previewTimer = setTimeout(advance, remaining * 1000);
      card.classList.add('is-active');
    };
    video.addEventListener('playing', schedule, options);
    video.addEventListener('waiting', () => {
      clearTimeout(this.previewTimer);
      clearTimeout(this.loadTimer);
      this.loadTimer = setTimeout(fail, 20000);
    }, options);
    video.addEventListener('timeupdate', () => {
      if (video.currentTime >= PREVIEW_SECONDS) advance();
    }, options);
    video.addEventListener('ended', advance, options);
    video.addEventListener('error', fail, options);
    video.muted = true;
    video.currentTime = 0;
    this.preloadVideo(this.index);
    this.loadTimer = setTimeout(fail, 20000);
    video.play().catch((error) => {
      if (generation !== this.generation) return;
      if (error.name === 'NotAllowedError') {
        this.paused = true;
        this.stopPreview();
      } else fail();
    });
  }

  handleClick(event) {
    if (!(event.target instanceof Element)) return;
    const open = event.target.closest('[data-video-open]');
    if (open && !open.disabled) {
      if (this.suppressClick && event.detail !== 0) {
        event.preventDefault();
        return;
      }
      this.openPlayer(open);
    } else if (event.target.closest('[data-video-close]')) {
      this.dialog.close();
    } else if (event.target === this.dialog) {
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right
        || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
    }
  }

  openPlayer(button) {
    const card = button.closest('[data-video-card]');
    const preview = card?.querySelector('[data-video-preview]');
    if (!preview || this.dialog.open) return;
    this.stopPreview();
    this.opener = button;
    const sources = card.querySelector('[data-video-sources]')?.content.querySelectorAll('source')
      ?? preview.querySelectorAll('source');
    this.player.replaceChildren(...[...sources].map((source) => source.cloneNode(true)));
    this.player.poster = preview.poster;
    this.player.muted = false;
    this.dialog.setAttribute(
      'aria-label',
      card.querySelector('[data-video-title]')?.textContent.trim() || 'Product video'
    );
    this.dialog.showModal();
    lockScroll(this.dialog);
    this.player.load();
    this.player.play().catch(() => {
      // Native controls remain available if another tap is required.
    });
  }

  closePlayer() {
    this.player.pause();
    this.player.replaceChildren();
    this.player.removeAttribute('src');
    this.player.load();
    unlockScroll(this.dialog);
    this.opener?.focus({ preventScroll: true });
    this.startPreview();
  }
}

if (!customElements.get('ss-product-video-carousel')) {
  customElements.define('ss-product-video-carousel', ProductVideoCarousel);
}
